import type { CityConfig } from "@/pages/city-template/types";

export const laughlinConfig: CityConfig = {
  city: "Laughlin",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "laughlin",
  route: "/cities/laughlin",
  title: "Emergency Passport Laughlin NV | Expedited Passport Colorado River Nevada",
  metaDescription: "Emergency passport and expedited passport services in Laughlin, NV — as fast as 24 hours. Fast Passport Center serves Laughlin, Bullhead City AZ, Fort Mohave, and the Colorado River tri-state region. Registered State Dept. courier. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Laughlin, Nevada",
  heroSubtitle: "Last-minute Mexico trip from Laughlin/Bullhead International? Family vacation to Canada and your passport is expired? Colorado River jet boat tour to Mexico without proper documents? Fast Passport Center helps Laughlin and the Colorado River region residents get expedited passports as fast as 24 hours — entirely remotely. The Los Angeles Passport Agency is 290 miles away, but we serve you without the desert drive.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Laughlin%20Nevada%20casino%20resort%20strip%20along%20the%20Colorado%20River%20at%20golden%20hour%20with%20sparkling%20river%20reflections%2C%20Don%20Laughlins%20Riverside%20Resort%20and%20other%20casino%20hotels%20lining%20the%20waterfront%2C%20the%20Colorado%20River%20separating%20Nevada%20from%20Arizona%20with%20the%20Black%20Mountains%20in%20the%20background%2C%20desert%20oasis%20resort%20town%20with%20palm%20trees%20and%20river%20activities%2C%20dramatic%20southwestern%20sunset%20colors%20reflecting%20on%20the%20river%2C%20no%20people%20or%20text%20visible&width=1800&height=700&seq=laughlin-nv-hero-01&orientation=landscape",
  heroImageAlt: "Laughlin Nevada casino resort strip along the Colorado River at sunset",
  geoPosition: "35.1678;-114.5728",
  geoPlacename: "Laughlin, Nevada",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd, Suite 1000, Los Angeles, CA 90024",
    distance: "About 290 miles west — approximately 4.5 hours via I-40 and I-15 through the Mojave Desert.",
  },
  nearbyCitiesZips: [
    { name: "Laughlin", zips: "89028, 89029" },
    { name: "Bullhead City", zips: "86429, 86430, 86442" },
    { name: "Fort Mohave", zips: "86426" },
    { name: "Mohave Valley", zips: "86440" },
    { name: "Golden Valley", zips: "86413" },
    { name: "Kingman", zips: "86401, 86409" },
    { name: "Needles", zips: "92363" },
    { name: "Lake Havasu City", zips: "86403, 86404, 86406" },
    { name: "Topock", zips: "86436" },
    { name: "Golden Shores", zips: "86436" },
    { name: "Dolan Springs", zips: "86441" },
    { name: "Oatman", zips: "86433" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Laughlin", desc: "Complete your emergency or expedited passport order from anywhere in the Colorado River region — no desert drive to Los Angeles required." },
    { icon: "ri-truck-line", title: "Overnight Shipping from Bullhead City or Kingman", desc: "Ship your documents from The UPS Store in Bullhead City or Kingman FedEx — both support overnight delivery for expedited submissions." },
    { icon: "ri-shield-check-line", title: "Tri-State Region Expertise", desc: "We understand the NV-AZ-CA border region's unique challenges — far from agencies, limited acceptance facilities, and resort community travel patterns." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We bypass the public appointment backlog with reserved Los Angeles Passport Agency slots — far faster than driving 4.5 hours through the desert." },
  ],
  testimonials: [
    {
      name: "David H.",
      location: "Laughlin, NV",
      rating: 5,
      test: "We booked a Mexico cruise out of Los Angeles — great deal through the Laughlin casino host program. Two weeks before departure, I realized my passport was expired. Fast Passport Center renewed it in 5 business days. Being in Laughlin, we're far from everything, but the overnight shipping from Bullhead City worked perfectly. Made our cruise.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Sharon M.",
      location: "Bullhead City, AZ",
      rating: 5,
      test: "My daughter's study abroad program in Spain had a last-minute opening and we had 10 days to get her passport. Fast Passport Center got it done in 6 business days from right across the river. The agent walked us through the Bullhead City Post Office acceptance and expedited everything. She made her flight.",
      service: "Emergency Passport Processing",
    },
    {
      name: "Mike R.",
      location: "Fort Mohave, AZ",
      rating: 5,
      test: "Needed child passports for our family vacation to Canada — a trip from the Colorado River to the Canadian Rockies. Fast Passport Center guided us through the acceptance step and got all three kids' passports in 7 business days. Fantastic service for the tri-state area.",
      service: "Child Passport Expedited",
    },
  ],
  facilities: [
    {
      id: "laughlin-post",
      name: "Laughlin Post Office",
      type: "USPS Passport Acceptance",
      address: "2900 S Needles Hwy",
      city: "Laughlin",
      state: "NV",
      zip: "89029",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "South Laughlin — primary passport acceptance for the Laughlin area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility for Laughlin residents. Appointment required. Availability can be limited during peak tourist season (December–March).",
      icon: "ri-mail-line",
    },
    {
      id: "bullhead-city-post",
      name: "Bullhead City Post Office",
      type: "USPS Passport Acceptance",
      address: "1882 Lakeside Dr",
      city: "Bullhead City",
      state: "AZ",
      zip: "86442",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Bullhead City — just across the Colorado River from Laughlin via the Laughlin Bridge",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Arizona acceptance facility directly across the river from Laughlin casinos. Excellent alternative — often better availability than Laughlin's post office.",
      icon: "ri-mail-line",
    },
    {
      id: "kingman-post",
      name: "Kingman Post Office",
      type: "USPS Passport Acceptance",
      address: "1901 Johnson Ave",
      city: "Kingman",
      state: "AZ",
      zip: "86401",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Kingman — about 35 miles east of Laughlin via AZ-68",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Larger facility in the Mohave County seat. Better appointment availability than Laughlin or Bullhead City. Good option if combining with Kingman errands.",
      icon: "ri-mail-line",
    },
    {
      id: "mohave-county-clerk",
      name: "Mohave County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "700 W Beale St",
      city: "Kingman",
      state: "AZ",
      zip: "86401",
      phone: "(928) 753-0713",
      hours: "Mon–Fri 8am–5pm",
      distance: "Kingman — Mohave County Superior Court, about 35 miles east of Laughlin via AZ-68",
      website: "https://www.mohave.gov/government/elected-officials/clerk-of-superior-court/",
      websiteLabel: "Mohave County Clerk",
      notes: "Mohave County Clerk in Kingman accepts passport applications. Additional option in Kingman beyond the post office.",
      icon: "ri-government-line",
    },
    {
      id: "lake-havasu-post",
      name: "Lake Havasu City Post Office",
      type: "USPS Passport Acceptance",
      address: "1750 McCulloch Blvd N",
      city: "Lake Havasu City",
      state: "AZ",
      zip: "86403",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Lake Havasu City — about 70 miles south of Laughlin via AZ-95",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Alternative acceptance facility for south Mohave County residents. Larger facility with more appointment options.",
      icon: "ri-mail-line",
    },
    {
      id: "bullhead-ups-store",
      name: "The UPS Store — Bullhead City",
      type: "Shipping & Document Services",
      address: "3699 Hwy 95, Suite 500",
      city: "Bullhead City",
      state: "AZ",
      zip: "86442",
      phone: "(928) 763-1800",
      hours: "Mon–Fri 8:30am–6pm, Sat 10am–3pm",
      distance: "Bullhead City — most convenient overnight shipping for Laughlin and Colorado River area residents",
      website: "https://locations.theupsstore.com/az/bullhead-city/3699-hwy-95",
      websiteLabel: "Schedule & Services",
      notes: "The UPS Store in Bullhead City handles overnight shipping for document submission. Does not accept passport applications but is essential for the expedited process.",
      icon: "ri-truck-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Laughlin, Nevada?",
      a: "Yes. Fast Passport Center handles emergency passport processing for Laughlin residents as fast as 24 hours. The Los Angeles Passport Agency is about 290 miles from Laughlin — we serve you entirely remotely via overnight shipping from Bullhead City or Kingman.",
    },
    {
      q: "How fast can I get an expedited passport from Laughlin, NV?",
      a: "Expedited passport processing from Laughlin takes 2–9 business days through Fast Passport Center. Most adult renewals don't require an in-person acceptance visit — ship overnight from The UPS Store in Bullhead City or Kingman FedEx.",
    },
    {
      q: "Where is the passport acceptance office in Laughlin?",
      a: "Laughlin's passport acceptance location is the Laughlin Post Office (2900 S Needles Hwy). The Bullhead City Post Office (1882 Lakeside Dr, across the river) and Kingman Post Office (35 miles east) have better appointment availability and are popular alternatives.",
    },
    {
      q: "Do I need a passport for jet boat tours from Laughlin to Mexico?",
      a: "Laughlin's Colorado River jet boat tours that cross into Mexican waters require valid U.S. passport documentation for re-entry to the United States. Don't assume a river excursion doesn't need a passport — check with your tour operator and have your passport ready.",
    },
    {
      q: "How far is Laughlin from the Los Angeles Passport Agency?",
      a: "Laughlin is approximately 290 miles from the Los Angeles Passport Agency — about 4.5 hours via I-40 and I-15 through the Mojave Desert. For most urgent travel needs, Fast Passport Center serves Laughlin entirely remotely.",
    },
    {
      q: "What airport do Laughlin residents use for international travel?",
      a: "Laughlin/Bullhead International Airport (IFP) offers limited regional service. For international flights, Harry Reid International (LAS) is about 100 miles north via US-95, and Phoenix Sky Harbor (PHX) is about 220 miles southeast. Both are major international hubs for Laughlin travelers.",
    },
    {
      q: "Does Laughlin's casino resort industry create passport needs?",
      a: "Yes — Laughlin's casino resorts employ seasonal and international workers, and the casino host/player reward programs frequently offer international travel packages (cruises, Mexico stays, European tours) as player incentives. These comp trips often come with short notice — ideal for expedited passport processing.",
    },
    {
      q: "Can I renew my passport from Laughlin without visiting an office?",
      a: "Yes. Most adult Laughlin residents renewing their passport can do so entirely by mail — no in-person acceptance visit required for renewals. Ship overnight from The UPS Store in Bullhead City or Kingman FedEx.",
    },
    {
      q: "Can I get a child passport from Laughlin?",
      a: "Yes. Child passports require an in-person acceptance visit — Laughlin families typically use the Laughlin Post Office or Bullhead City Post Office. Both parents must typically be present.",
    },
    {
      q: "How much does expedited passport processing cost from Laughlin?",
      a: "Fast Passport Center pricing includes the U.S. government passport fees plus our courier service fee — which is typically 30–100% lower than FedEx Office or retail passport services.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Bullhead City", address: "2350 Miracle Mile, Bullhead City, AZ 86442", hours: "Daily 8 AM – 10 PM" },
    { name: "The UPS Store — Bullhead City", address: "3699 Hwy 95, Suite 500, Bullhead City, AZ 86442", hours: "Mon–Fri 8:30 AM – 6 PM, Sat 10 AM – 3 PM" },
    { name: "Walmart Supercenter — Bullhead City", address: "6000 Hwy 95, Bullhead City, AZ 86426", hours: "Daily 6 AM – 11 PM" },
    { name: "FedEx Office — Kingman", address: "3390 Stockton Hill Rd, Kingman, AZ 86409", hours: "Mon–Fri 8 AM – 6 PM, Sat 9 AM – 3 PM" },
    { name: "CVS Pharmacy — Bullhead City", address: "multiple locations on Hwy 95", hours: "Most open until 9 PM or later" },
    { name: "Laughlin Post Office", address: "2900 S Needles Hwy, Laughlin, NV 89029", hours: "Mon–Fri 9 AM – 5 PM" },
    { name: "Walgreens — Kingman", address: "3490 Stockton Hill Rd, Kingman, AZ 86409", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart Supercenter — Kingman", address: "3396 Stockton Hill Rd, Kingman, AZ 86409", hours: "Daily 6 AM – 11 PM" },
  ],
  dmvLocations: [
    {
      name: "Bullhead City DMV (Arizona MVD)",
      address: "1130 Hancock Rd, Bullhead City, AZ 86442",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Arizona MVD in Bullhead City — the closest DMV for Laughlin residents (across the river). Serves Arizona residents primarily, but Laughlin NV residents can visit for information.",
      featured: true,
    },
    {
      name: "Kingman DMV (Arizona MVD)",
      address: "3675 E Andy Devine Ave, Kingman, AZ 86401",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Arizona MVD in Kingman — about 35 miles from Laughlin. Larger facility with full services.",
      featured: false,
    },
    {
      name: "Las Vegas DMV — Flamingo",
      address: "8250 W Flamingo Rd, Las Vegas, NV 89147",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Nevada DMV full-service location in Las Vegas — about 100 miles from Laughlin via US-95. For Nevada DMV services.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "IFP",
      name: "Laughlin/Bullhead International Airport",
      distance: "Local airport — on the Arizona side of the Colorado River, about 5 miles from Laughlin",
      address: "2550 Laughlin View Dr, Bullhead City, AZ 86429",
      note: "Laughlin/Bullhead International Airport offers limited regional service. No regular international flights — for international travel, use LAS or PHX.",
      flightUrl: "https://www.flyifp.com/",
      flightLabel: "IFP Airport Website",
      featured: true,
    },
    {
      code: "LAS",
      name: "Harry Reid International Airport (Las Vegas)",
      distance: "Primary international hub — about 100 miles north via US-95 (approximately 1 hour 45 minutes)",
      address: "5757 Wayne Newton Blvd, Las Vegas, NV 89119",
      note: "Harry Reid International is the primary international departure hub for Laughlin travelers. LAS offers direct flights to Mexico, Canada, Europe, Asia, and Central America.",
      flightUrl: "https://www.harryreidairport.com/",
      flightLabel: "LAS Airport Website",
      featured: true,
    },
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "About 220 miles southeast via US-93 and I-10 (approximately 3.5 hours)",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Major Southwest/American hub with extensive domestic and international connections. Alternative for Laughlin travelers heading to Texas, Southeast US, or Mexico.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Laughlin & Colorado River Region",
      description: "Casino comp trip to Mexico departing soon? Family emergency overseas? Fast Passport Center handles emergency passport processing for Laughlin residents — as fast as 24 hours.",
      bullets: ["As fast as 24 hours processing", "Reserved Los Angeles agency submission", "Dedicated agent and live status tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired and you're 100 miles from Vegas and 290 from LA? Laughlin residents renew fast through Fast Passport Center — ship overnight from Bullhead City or Kingman.",
      bullets: ["Fastest renewal for Colorado River region", "Ship overnight from Bullhead City or Kingman", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport in Laughlin or before a casino comp trip abroad? We guide Colorado River travelers through DS-64 and expedited replacement.",
      bullets: ["DS-64 theft/loss reporting guidance", "Expedited replacement timelines available", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Laughlin Families",
      description: "Laughlin and Bullhead City families heading to Mexico, Canada, or beyond need child passports. We guide you through the Laughlin or Bullhead City Post Office acceptance step.",
      bullets: ["Both parents typically required to consent", "Laughlin or Bullhead City Post Office guidance", "Expedited after in-person acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First passport from Laughlin? In-person acceptance at the Laughlin or Bullhead City Post Office is required for first-timers. We guide you through every step.",
      bullets: ["Step-by-step acceptance guidance for Laughlin and Bullhead City", "Photo location guide for Colorado River region", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels at 30–100% lower prices.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Specialty Passport Services",
      description: "Name change passports, damaged passport replacement, second passports for frequent travelers, and Laughlin residents with complex passport needs.",
      bullets: ["Name change after marriage", "Damaged passport replacement", "Second passport for frequent international travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Laughlin, Nevada residents with urgent international travel needs." },
    { name: "Expedited Passport", description: "Expedited passport processing for Laughlin and Colorado River region residents in 2–9 business days." },
    { name: "Passport Renewal", description: "Fast passport renewal for Laughlin, Nevada — ship overnight from Bullhead City or Kingman." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Laughlin travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Laughlin and Colorado River area families." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier serving Laughlin, Nevada and the Colorado River tri-state region." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Booking Casino Comp International Trips Without Checking Passport Validity", description: "Laughlin casino hosts frequently offer international travel packages (cruises, Mexico stays) as comps. These offers often have tight booking windows — and expired passports are commonly discovered after accepting the comp." },
    { icon: "ri-camera-off-line", title: "Submitting Poor-Quality Passport Photos", description: "Photos from Laughlin's limited retail options can get rejected. Fast Passport Center reviews photo specifications before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms or Missing Signatures", description: "One missing signature returns your application — especially bad when you're 290 miles from the nearest agency. Our review catches everything." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Format", description: "Short-form birth certificates are rejected. We specify exactly what document format is needed based on your state of birth." },
    { icon: "ri-map-pin-time-line", title: "Snowbird Passport Expiration During Extended Winter Stays", description: "Laughlin's large snowbird population often stays 3–6 months. A passport that's valid at departure may expire during the stay — check validity through your return date plus buffer." },
    { icon: "ri-ship-line", title: "Assuming Colorado River Jet Boat Tours Don't Need Passports", description: "Some Laughlin jet boat excursions cross into Mexican waters. Re-entry to the U.S. requires valid passport documentation. Always check with your tour operator." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Laughlin travel date. Complete your secure order from home in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized document checklist — with Laughlin and Bullhead City acceptance location guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight", description: "Drop off at The UPS Store in Bullhead City or Kingman FedEx Office for overnight delivery." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Los Angeles Passport Agency slots — bypassing the public appointment backlog." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Door", description: "Your completed passport ships directly to your Laughlin or Colorado River area address. Live status tracking included." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Los Angeles — 4.5+ hours from Laughlin",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship overnight from Bullhead City or Kingman",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than USPS self-service",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail from rural Nevada",
        "6–13 weeks; can be longer from Laughlin",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — weeks-long wait, Los Angeles only (290 miles)", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Drive to Los Angeles — 4.5+ hours via I-40 and I-15", fpc: "Ship overnight from Bullhead City or Kingman" },
    { feature: "Processing Speed", agency: "Strict eligibility requirements", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Tri-State Region Accessibility", agency: "Not designed for rural border community residents", fpc: "Fully remote service via overnight shipping" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 9-hour LA round trip through desert", fpc: "Best price guarantee — no travel required" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Las Vegas", route: "/cities/las-vegas" },
    { name: "Emergency passport help in Henderson", route: "/cities/henderson-nv" },
    { name: "Fast passport renewal for Nevada residents", route: "/states/nevada" },
    { name: "Rush passport services in Bullhead City area", route: "/states/nevada" },
    { name: "Expedited passports statewide in Nevada", route: "/states/nevada" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from The UPS Store in Bullhead City or Kingman FedEx Office. No visit to our processing facility required.",
  fpcOfficeDistance: "Serving Laughlin, Bullhead City, and the entire Colorado River tri-state region remotely",
  serviceAreaIntro: "Whether you are in Laughlin, Bullhead City, Fort Mohave, Mohave Valley, Golden Valley, or anywhere in the Colorado River tri-state region — Fast Passport Center makes emergency and expedited passport processing simple without a desert drive to Los Angeles.",
  nearbyCitiesIntro: "We serve all Laughlin and Colorado River area zip codes including Bullhead City, Fort Mohave, Mohave Valley, Golden Valley, Kingman, and Needles.",
  howItWorksIntro: "Getting an emergency or expedited passport from Laughlin is simpler than a 4.5-hour drive through the Mojave Desert. Most Colorado River region clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Laughlin and Colorado River area passport applicants. We review your documents before submission to prevent all of them.",
  testimonialsIntro: "Real reviews from Laughlin, Bullhead City, and Colorado River region travelers who needed their passports fast.",
  faqIntro: "The most common questions Laughlin and Colorado River region residents ask about emergency and expedited passport services.",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (Nevada Vital Records)", url: "https://dpbh.nv.gov/Programs/VitalRecords/", desc: "Nevada Division of Public & Behavioral Health — Vital Records for birth and death certificates statewide" },
    { label: "Marriage Licenses (Clark County Clerk)", url: "https://www.clarkcountynv.gov/government/elected_officials/county_clerk/", desc: "Clark County Clerk for marriage license records" },
    { label: "Divorce Decrees (Clark County District Court)", url: "https://www.clarkcountycourts.us/", desc: "Clark County District Court for divorce decree records" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Nevada vital records online via VitalChek" },
  ],
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  travelTiming: {
    title: "Travel & Passport Timing for Laughlin and Colorado River Region Residents",
    intro: "Laughlin is Nevada's southernmost casino resort town — perched on the Colorado River opposite Bullhead City, Arizona. Its identity as a casino destination, snowbird haven, and tristate border community creates distinctive international travel patterns unique in Nevada.",
    cards: [
      {
        icon: "ri-vip-crown-line",
        title: "Casino Comp International Travel — the Laughlin Factor",
        subtitle: "Casino host reward programs drive unique, short-notice passport needs",
        description: "Laughlin's casino resorts use international travel packages as player rewards — comped cruises to the Caribbean and Alaska, Mexico resort stays, and European tours. These comp offers typically come with 2–6 weeks booking windows, creating a pattern of short-notice passport urgency. Casino hosts call with a 'you earned a trip to Cancun' offer on Tuesday, and the client discovers their passport expired on Wednesday. Fast Passport Center's 2–9 business day processing is built for exactly these situations.",
        tip: "If you're a regular Laughlin casino player receiving comp offers, maintain a valid passport at all times. The best comp offers have the shortest booking windows — and you don't want to miss a free cruise because your passport expired.",
      },
      {
        icon: "ri-sun-line",
        title: "Snowbird Winter Migration",
        subtitle: "Laughlin's seasonal population follows predictable travel patterns",
        description: "Like many Colorado River communities, Laughlin has a large snowbird population — retirees who winter in the mild desert climate (November–April) and travel internationally during shoulder seasons. Mexico (San Carlos, Puerto Vallarta, Mazatlan) and Costa Rica are the most popular snowbird winter destinations. Passport renewals peak in September–October as snowbirds prepare for winter migration. Multi-month stays require careful passport validity planning.",
        tip: "Snowbirds: check that your passport is valid for your entire stay plus 6 months beyond your return. Many snowbirds discover passport issues weeks before their November departure — start the renewal process by August.",
      },
      {
        icon: "ri-ship-2-line",
        title: "Colorado River — International Border Adventures",
        subtitle: "Jet boat tours and river excursions cross into Mexico",
        description: "A uniquely Laughlin phenomenon — Colorado River jet boat excursions that travel south through the Topock Gorge and can cross into Mexican waters. These popular tourist activities require valid U.S. passport documentation for re-entry. Many Laughlin visitors and residents don't realize a river excursion can cross an international border — and discover this requirement at the dock.",
        tip: "If you're booking a Colorado River jet boat tour, ask the operator whether the route crosses into Mexican waters. If yes, you need valid U.S. passport documentation for re-entry — even for a day trip.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "Dual Gateway — LAS and PHX for International Travel",
        subtitle: "Two major airports within driving distance serve the Laughlin region",
        description: "Laughlin residents have access to two major international airports: Harry Reid International (LAS, 100 miles north) and Phoenix Sky Harbor (PHX, 220 miles southeast). LAS is closer and offers direct flights to Mexico, Canada, Europe, and Asia. PHX is a Southwest and American Airlines hub with more domestic connections and some international routes. This dual-airport access gives Laughlin travelers flexibility in international departure options.",
        tip: "Compare LAS and PHX for international flights. LAS is closer (100 miles) but PHX sometimes has better fares to Texas, the Southeast, and Mexico destinations. Both airports are within a half-day drive from Laughlin.",
      },
    ],
    callout: {
      icon: "ri-vip-crown-line",
      title: "Laughlin, Nevada — Casino Paradise on the Colorado River",
      description: "Laughlin sits on the banks of the Colorado River at Nevada's southern tip — a casino resort town where the hotels face the water and Arizona is just across the bridge. Its residents — from casino employees and snowbirds to Colorado River adventurers — create a distinctive international travel profile shaped by casino comp offers, seasonal migration, and the unique geography of the tristate border. Fast Passport Center has served Laughlin and Colorado River residents with expedited passports for comp trips, winter migrations, and spontaneous international adventures.",
    },
  },
  destinations: {
    title: "Where Laughlin Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Laughlin and Colorado River region clients. Casino comp offers, snowbird migration, jet boat border crossings, and dual-airport access create distinctive passport urgency situations in Nevada's southernmost community.",
    stats: [
      { stat: "5 mi", label: "to Laughlin/Bullhead International Airport (IFP) — local regional airport" },
      { stat: "100 mi", label: "to Harry Reid International (LAS) — primary international gateway via US-95" },
      { stat: "290 mi", label: "to Los Angeles Passport Agency — 4.5+ hours through the Mojave Desert" },
      { stat: "2–9", label: "business days for Fast Passport Center expedited processing from Laughlin" },
    ],
    destinations: [
      { name: "Mexico — Puerto Vallarta, Cancun, Cabo", context: "Top casino comp destination and snowbird winter escape — direct flights from LAS", urgency: "Comp offers arrive 2–4 weeks before departure; snowbird bookings 4–8 weeks out", icon: "ri-sun-cloudy-line" },
      { name: "Caribbean & Alaska Cruises", context: "Casino comp cruises are the #1 reward — Caribbean from Florida ports, Alaska from Seattle", urgency: "Comp cruise offers come with 2–6 week booking windows — among the shortest-notice passport needs", icon: "ri-ship-2-line" },
      { name: "Canada — Vancouver, Toronto, Calgary", context: "Direct flights from LAS make Canada accessible; snowbirds sometimes summer in BC", urgency: "Booked 4–8 weeks out; passport required for all air travel to Canada", icon: "ri-map-pin-2-line" },
      { name: "Costa Rica & Panama — Winter Eco-Adventures", context: "Snowbird alternatives to Mexico — active retiree community favors Central American eco-travel", urgency: "October–November booking for December–March stays; 3–5 weeks before departure", icon: "ri-leaf-line" },
      { name: "European Escorted Tours", context: "Casino rewards include European tours; retiree community books escorted European trips", urgency: "Comp trips booked 3–8 weeks out; self-booked tours 4–12 weeks out", icon: "ri-global-line" },
      { name: "Mexico Border — Colorado River Jet Boat Day Trips", context: "Unique Laughlin phenomenon — river excursions crossing into Mexican waters", urgency: "Day-trip bookings; passport requirement often discovered at dock — have it ready before booking", icon: "ri-anchor-line" },
    ],
    crossLinksTitle: "Explore Nearby Nevada Service Areas",
    crossLinksDesc: "We serve travelers throughout southern Nevada and the Colorado River region with the same expedited passport expertise.",
    crossLinks: [
      { name: "Las Vegas", route: "/cities/las-vegas" },
      { name: "Henderson", route: "/cities/henderson-nv" },
      { name: "Reno", route: "/cities/reno" },
    ],
  },
};